import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { loadUser } from './store/slices/authSlice';
import socketService from './services/socket';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AdminLayout from './components/layouts/AdminLayout';
import SupplierLayout from './components/layouts/SupplierLayout';
import CustomerLayout from './components/layouts/CustomerLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import ProductsPage from './pages/public/ProductsPage';
import ProductDetailPage from './pages/public/ProductDetailPage';
import AIAssistantPage from './pages/public/AIAssistantPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import SupplierInvitePage from './pages/auth/SupplierInvitePage';
import TrackShipmentPage from './pages/public/TrackShipmentPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSuppliers from './pages/admin/AdminSuppliers';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminContentApproval from './pages/admin/AdminContentApproval';
import AdminViolations from './pages/admin/AdminViolations';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminEscrow from './pages/admin/AdminEscrow';
import AdminSettings from './pages/admin/AdminSettings';
import AdminProductRequests from './pages/admin/AdminProductRequests';
import AdminCustomizationRequests from './pages/admin/AdminCustomizationRequests';
import AdminProfile from './pages/admin/AdminProfile';

// Supplier Pages
import SupplierDashboard from './pages/supplier/SupplierDashboard';
import SupplierProducts from './pages/supplier/SupplierProducts';
import SupplierProductDetail from './pages/supplier/SupplierProductDetail';
import SupplierOrders from './pages/supplier/SupplierOrders';
import SupplierQuotes from './pages/supplier/SupplierQuotes';
import SupplierChat from './pages/supplier/SupplierChat';
import SupplierCustomizationRequests from './pages/supplier/SupplierCustomizationRequests';

// Customer Pages
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerOrders from './pages/customer/CustomerOrders';
import CustomerOrderDetail from './pages/customer/CustomerOrderDetail';
import CustomerQuotes from './pages/customer/CustomerQuotes';
import CustomerCart from './pages/customer/CustomerCart';
import CustomerChat from './pages/customer/CustomerChat';
import CustomerProfile from './pages/customer/CustomerProfile';
import CustomerPaymentMethods from './pages/customer/CustomerPaymentMethods';
import CustomerCustomizationRequests from './pages/customer/CustomerCustomizationRequests';
import CustomizationRequestDetail from './pages/customer/CustomizationRequestDetail';

// Loading
import { CircularProgress, Box } from '@mui/material';

const PrivateRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, user } = useSelector((state) => state.auth);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/" />;

  return children;
};

function App() {
  const dispatch = useDispatch();
  const { token, isAuthenticated, user } = useSelector((state) => state.auth);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (token) {
      dispatch(loadUser());
    }
  }, [dispatch, token]);

  // Sync i18n with user's preferred language once authenticated
  useEffect(() => {
    const preferred = user?.preferences?.language;
    if (preferred && i18n.language !== preferred) {
      i18n.changeLanguage(preferred);
    }
  }, [user, i18n]);

  useEffect(() => {
    if (isAuthenticated && token) {
      socketService.connect(token);
    }
    return () => socketService.disconnect();
  }, [isAuthenticated, token]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<HomePage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="track/:number" element={<TrackShipmentPage />} />
        <Route path="ai-assistant" element={<PrivateRoute><AIAssistantPage /></PrivateRoute>} />
      </Route>

      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/supplier-invite/:token" element={<SupplierInvitePage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <PrivateRoute roles={['admin']}><AdminLayout /></PrivateRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="suppliers" element={<AdminSuppliers />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="content-approval" element={<AdminContentApproval />} />
        <Route path="violations" element={<AdminViolations />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="escrow" element={<AdminEscrow />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="product-requests" element={<AdminProductRequests />} />
        <Route path="customization-requests" element={<AdminCustomizationRequests />} />
        <Route path="profile" element={<AdminProfile />} />
      </Route>

      {/* Supplier Routes */}
      <Route path="/supplier" element={
        <PrivateRoute roles={['supplier_employee']}><SupplierLayout /></PrivateRoute>
      }>
        <Route index element={<SupplierDashboard />} />
        <Route path="products" element={<SupplierProducts />} />
        <Route path="products/:id" element={<SupplierProductDetail />} />
        <Route path="orders" element={<SupplierOrders />} />
        <Route path="quotes" element={<SupplierQuotes />} />
        <Route path="chat" element={<SupplierChat />} />
        <Route path="customization-requests" element={<SupplierCustomizationRequests />} />
      </Route>

      {/* Customer Routes */}
      <Route path="/customer" element={
        <PrivateRoute roles={['customer']}><CustomerLayout /></PrivateRoute>
      }>
        <Route index element={<CustomerDashboard />} />
        <Route path="orders" element={<CustomerOrders />} />
        <Route path="orders/:id" element={<CustomerOrderDetail />} />
        <Route path="quotes" element={<CustomerQuotes />} />
        <Route path="cart" element={<CustomerCart />} />
          <Route path="payment-methods" element={<CustomerPaymentMethods />} />
        <Route path="chat" element={<CustomerChat />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="customization-requests" element={<CustomerCustomizationRequests />} />
        <Route path="customization-requests/:id" element={<CustomizationRequestDetail />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
